import { ReloadOutlined } from "@ant-design/icons";
import { Button, Spin, Tooltip } from "antd";
import classNames from "classnames";
import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useProject } from "../../App/state";
import { useNotification } from "../../contexts/Notifications";
import {
  useGetMappingsOnSpaceLazyQuery,
  useGetShotsOnSceneLazyQuery,
  useGetSpacesTreeWithDescendantSpacesQuery,
  useSetSceneParentMutation,
  useSetSpaceParentMutation,
  useUpdateScenesMutation,
  useUpdateSpacesMutation,
} from "../../generated/types";
import { reorderNodes } from "../../utils/list-utils";
import { DisplayTree } from "../DisplayTree/DisplayTree";
import { TreeWithTagsSwitch } from "../DisplayTree/TreeWithTagsSwitch";
import { findNode } from "../DisplayTree/display-tree-utils";
import { ROOT_PARENT_KEY, findSearchHits } from "../DisplayTree/search-utils";
import { DisplayTreeNode, DisplayTreeNodeType } from "../DisplayTree/types";
import { SearchBar, SpaceSearchInput } from "./Search/SearchBar";
import { matches, searchIsEmpty } from "./Search/search-utils";
import styles from "./SpacesTree.module.css";
import { SpacesTreeNodeReparentingModal } from "./SpacesTreeNodeReparentingModal";
import {
  MAPPINGS_FOLDER_NAME,
  MAPPINGS_KEY,
  SCENES_FOLDER_NAME,
  Scene,
  Shot,
  Space,
  buildSpacesTree,
  find,
  generateFolderName,
  getMappingFolderDisplayTreeNode,
  getSpacesByIds,
  nodeFolderKeyToId,
  transformMappingsToDisplayTreeNodes,
  transformShotsToDisplayTreeNode,
  updateTree,
} from "./tree-nodes";

interface SpacesTreeProps {
  rootSpaceIds?: string[];
  onSelect?: (selectedNode: DisplayTreeNode) => void;
  selectedKeys?: string[];
  allowMultiSelect?: boolean;
  onLoadSpacesTree?: (spacesByIds: Record<string, Space>) => void;
  onCheck?: (nodes: DisplayTreeNode[]) => void;
  checkable?: boolean;
  setNodesToBeDeleted?: (nodes: string[]) => void;
  scrollable?: boolean;
  editable?: boolean;
  draggableScenes?: boolean;
  searchable?: boolean;
  showMappings?: boolean;
  showScenes?: boolean;
  showShots?: boolean;
  showTagsSwitch?: boolean;
  showMetadata?: boolean;
  coloredTags?: boolean;
  onLoadShots?: (shots: Shot[]) => void;
  nodeKeysToUpdate?: string[];
  onNodeKeysUpdated?: (keys: string[]) => void;
  version?: number;
  setVersion?: (number: number) => void;
  setTreeState?: (tree: DisplayTreeNode[]) => void;
  externalSearchHits?: { keys: string[]; parentKeys: string[] };
  // Let the tree fill its container's height. Only enable where an ancestor
  // passes a definite height down (e.g. the config columns); otherwise the tree
  // uses a fixed comfortable height instead of shrinking to a short panel.
  fillHeight?: boolean;
}

const NAME_COLLISION_ERROR_MESSAGE =
  "A scene with this name already exists within the space. Please change the name of one of the scenes to make sure there are no duplicate names.";

const SpacesTreeComponent = forwardRef<
  { loadMappingsForSpaces: () => void },
  SpacesTreeProps
>(
  (
    {
      rootSpaceIds,
      onSelect,
      selectedKeys,
      allowMultiSelect,
      onLoadSpacesTree,
      onCheck,
      checkable,
      scrollable,
      editable,
      draggableScenes,
      searchable,
      showMappings,
      showScenes,
      showShots,
      showMetadata,
      coloredTags,
      showTagsSwitch = false,
      onLoadShots,
      nodeKeysToUpdate,
      onNodeKeysUpdated,
      version,
      setVersion,
      setTreeState,
      externalSearchHits,
      fillHeight,
    },
    ref,
  ) => {
    const [showTags, setShowTags] = useState(showTagsSwitch);
    const [searchInput, setSearchInput] = useState<SpaceSearchInput>();
    const [spacesTree, setSpacesTree] = useState<DisplayTreeNode[]>([]);
    const [treeLoading, setTreeLoading] = useState(false);
    const [confirmationModalVisible, setConfirmationModalVisible] =
      useState<boolean>(false);
    const [draggedNodeInfo, setDraggedNodeInfo] = useState<{
      id: string;
      name: string;
      nodeType: DisplayTreeNodeType.Scene | DisplayTreeNodeType.Space;
    }>();
    const [newParent, setNewParent] = useState<{
      id: string;
      name: string;
    } | null>();
    const notify = useNotification();
    const project = useProject();
    const {
      data,
      refetch,
      loading: queryLoading,
    } = useGetSpacesTreeWithDescendantSpacesQuery({
      variables: {
        ...project,
        spaceFilters: rootSpaceIds
          ? { id: rootSpaceIds }
          : { parentSpaceId: null },
        includeMetadata: showMetadata ?? false,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "network-only",
    });

    const handleRefresh = useCallback(async () => {
      setTreeLoading(true);
      try {
        await refetch();
        notify("Spaces refreshed successfully", "success");
      } catch (error) {
        notify(`Failed to refresh tree data: ${error}`, "error");
      } finally {
        setTreeLoading(false);
      }
    }, [refetch, notify]);

    const allSpaces = useMemo(
      () =>
        data?.spacesByFilter.flatMap((rootSpace) => [
          _.omit(rootSpace, "descendantSpaces"),
          ...rootSpace.descendantSpaces,
        ]) ?? [],
      [data?.spacesByFilter],
    );

    const spacesByIds = useMemo(() => getSpacesByIds(allSpaces), [allSpaces]);

    // Move the callback into a useEffect to avoid setting state during render
    useEffect(() => {
      if (onLoadSpacesTree) {
        onLoadSpacesTree(spacesByIds);
      }
    }, [onLoadSpacesTree, spacesByIds]);

    const rootSpaces = useMemo(
      () =>
        rootSpaceIds
          ? Object.values(spacesByIds).filter((s) =>
              rootSpaceIds.includes(s.id),
            )
          : Object.values(spacesByIds).filter(
              (s) => s.parentId === ROOT_PARENT_KEY,
            ),
      [spacesByIds, rootSpaceIds],
    );
    const tree = useMemo(
      () =>
        buildSpacesTree(
          rootSpaces,
          spacesByIds,
          undefined,
          [],
          showMappings,
          showScenes,
          showShots,
          spacesTree ? spacesTree[0]?.version ?? 1 : 1,
          checkable,
          showTags,
          showMetadata,
          draggableScenes,
          coloredTags,
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        rootSpaces,
        spacesByIds,
        showMappings,
        showScenes,
        showShots,
        checkable,
        showTags,
        showMetadata,
        draggableScenes,
        coloredTags,
      ],
    );

    useEffect(() => {
      setTreeState?.(tree[0]?.children ?? []);
    }, [tree, setTreeState]);

    useEffect(() => {
      if (version === 1 || version === undefined) {
        setSpacesTree(tree);
      } else {
        if (
          tree[0] &&
          tree[0].version &&
          spacesTree[0] &&
          spacesTree[0].version
        ) {
          if (tree[0].version > spacesTree[0].version) {
            setSpacesTree(tree);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tree]);

    const handleOnCheck = useCallback(
      (strings: string[]) => {
        const nodes = strings.map((key) => find(spacesTree, key));
        const foundNodes = nodes.filter(
          (n) => n !== undefined,
        ) as DisplayTreeNode[];
        onCheck && onCheck(foundNodes);
      },
      [spacesTree, onCheck],
    );

    interface ReparentAction {
      action: "reparent";
      newParentId: string | null;
    }

    interface ReorderAction {
      action: "reorder";
      draggedNode: DisplayTreeNode;
      parentNode: DisplayTreeNode | null;
      parentNodeChildren: DisplayTreeNode[];
    }

    const checkNodeCanBeDropped = (
      draggedNode: DisplayTreeNode,
      targetNode: DisplayTreeNode,
      targetIsSibling: boolean, // true if the node is dropped next to the target node, false if it's being dropped "inside" the parent node
    ): ReorderAction | ReparentAction | undefined => {
      const parentNode = find(spacesTree, draggedNode.parentKey);

      let parentNodeChildren: DisplayTreeNode[] = [];
      if (draggedNode?.parentKey === ROOT_PARENT_KEY) {
        // The dragged node is a root node, hence the list of children is the inital data.
        parentNodeChildren = spacesTree;
      } else {
        parentNodeChildren = parentNode?.children ?? [];
      }
      if (!parentNodeChildren.length) {
        // This should never be reached.
        throw new Error(
          `Children not found for parent of node ${draggedNode?.key}`,
        );
      }

      const action = // if the target node is not the parent, check that either it is not a sibling,
        // or it is a sibling and it is dropped "inside" it rather than next to it (aka !targetIsSibling)
        (parentNode?.key !== targetNode.key &&
          (!parentNodeChildren.map((n) => n.key).includes(targetNode.key) ||
            !targetIsSibling)) ||
        // if the node is moved to become a sibling of its parent
        (parentNode?.key === targetNode.key && targetIsSibling)
          ? "reparent"
          : "reorder";

      switch (draggedNode?.nodeType) {
        case DisplayTreeNodeType.Space:
          if (targetNode?.nodeType !== DisplayTreeNodeType.Space) {
            notify(
              `Cannot ${action} a space to a ${targetNode?.nodeType}.`,
              "error",
            );
            return undefined;
          }
          break;
        case DisplayTreeNodeType.Scene: {
          const targetIsUnderScene =
            !targetIsSibling &&
            targetNode?.nodeType === DisplayTreeNodeType.Scene;
          if (targetIsUnderScene) {
            notify(`Cannot reparent a scene to another scene.`, "error");
            return undefined;
          }
          const targetIsNotUnderScenesFolderOrSpace =
            targetNode?.name !== SCENES_FOLDER_NAME &&
            targetNode?.nodeType !== DisplayTreeNodeType.Folder &&
            targetNode?.nodeType !== DisplayTreeNodeType.Scene &&
            targetNode?.nodeType !== DisplayTreeNodeType.Space;
          if (targetIsNotUnderScenesFolderOrSpace) {
            notify(
              `Cannot reparent a scene to a ${targetNode?.nodeType}.`,
              "error",
            );
            return undefined;
          }
          break;
        }
        case DisplayTreeNodeType.Folder:
          notify(`Cannot ${action} folders.`, "error");
          return undefined;
        case DisplayTreeNodeType.Mapping:
          notify(`Cannot ${action} mappings.`, "error");
          return undefined;
        default:
          // this indicates a bug in the code
          throw new Error(
            `Unexpected type ${draggedNode?.nodeType} for dragged node.`,
          );
      }

      // check if this is a reparenting operation
      if (action === "reparent") {
        let newParentId;
        if (draggedNode.nodeType === DisplayTreeNodeType.Scene) {
          if (targetNode.nodeType === DisplayTreeNodeType.Space) {
            newParentId = targetIsSibling
              ? targetNode.parentKey
              : targetNode.key;
          } else {
            const targetNodeParentNode = find(spacesTree, targetNode.parentKey);

            // Handle name collision error if dragging node would cause duplicate names under a space
            const reparentingHasNameCollision =
              !!targetNodeParentNode?.children?.find(
                (siblingNode) => siblingNode.name === draggedNode.name,
              );

            if (reparentingHasNameCollision) {
              notify(NAME_COLLISION_ERROR_MESSAGE, "error");
              return undefined;
            }

            // the immediate parent of a scene is a Scenes folder, we want the spaceId which is the parent of that Scenes folder
            if (targetIsSibling) {
              newParentId = targetNodeParentNode?.parentKey;
            } else {
              newParentId = targetNodeParentNode?.key;
            }
          }
        } else {
          newParentId = targetIsSibling ? targetNode.parentKey : targetNode.key;
        }
        return {
          action: "reparent",
          newParentId: newParentId === ROOT_PARENT_KEY ? null : newParentId,
        };
      }

      return {
        action: "reorder",
        draggedNode,
        parentNode: parentNode ?? null,
        parentNodeChildren,
      };
    };

    const [updateSpaces] = useUpdateSpacesMutation({
      onCompleted: () => {
        notify("Successfully reordered spaces", "success");
        refetch();
        setTreeLoading(false);
      },
      onError: (err) => {
        notify(`Error reordering spaces - ${err}`, "error");
        setTreeLoading(false);
      },
    });

    const [updateScenes] = useUpdateScenesMutation({
      onCompleted: () => {
        notify("Successfully reordered scenes", "success");
        refetch();
        setTreeLoading(false);
      },
      onError: (err) => {
        notify(`Error reordering scenes - ${err}`, "error");
        setTreeLoading(false);
      },
    });

    const [updateSpaceParent] = useSetSpaceParentMutation({
      onCompleted: () => {
        notify("Successfully reparented space", "success");
        refetch();
        setTreeLoading(false);
      },
      onError: (err) => {
        notify(`Error reparenting space - ${err}`, "error");
        setTreeLoading(false);
      },
      update: (cache) => {
        cache.evict({ fieldName: "spacesByFilter" });
        cache.gc();
      },
    });

    const [updateSceneParent] = useSetSceneParentMutation({
      onCompleted: () => {
        notify("Successfully reparented scene", "success");
        refetch();
        setTreeLoading(false);
      },
      onError: (err) => {
        notify(`Error reparenting scene - ${err}`, "error");
        setTreeLoading(false);
      },
      update: (cache) => {
        cache.evict({ fieldName: "spacesByFilter" });
        cache.gc();
      },
    });

    const updateTreeForReorder = useCallback(
      (parentNodeKey: string | null, newNodeOrder: DisplayTreeNode[]) => {
        setSpacesTree((prevTree) => {
          const newTree = [...prevTree];

          if (parentNodeKey === ROOT_PARENT_KEY || parentNodeKey === null) {
            return newNodeOrder.map((node, index) => ({
              ...node,
              guiIndex: index,
            }));
          }

          const parentNode = find(newTree, parentNodeKey);
          if (!parentNode) {
            return prevTree;
          }

          parentNode.children = newNodeOrder.map((node, index) => ({
            ...node,
            guiIndex: index,
          }));

          return newTree;
        });
      },
      [],
    );

    const updateTreeForReparent = useCallback(
      (
        draggedNodeKey: string,
        oldParentKey: string,
        newParentKey: string | null,
      ) => {
        setSpacesTree((prevTree) => {
          const newTree = [...prevTree];

          const draggedNode = find(newTree, draggedNodeKey);
          if (!draggedNode) {
            return prevTree;
          }

          const oldParentNode =
            oldParentKey === ROOT_PARENT_KEY
              ? null
              : find(newTree, oldParentKey);

          if (oldParentNode) {
            oldParentNode.children =
              oldParentNode.children?.filter(
                (child) => child.key !== draggedNodeKey,
              ) || [];
          } else if (oldParentKey === ROOT_PARENT_KEY) {
            const rootIndex = newTree.findIndex(
              (node) => node.key === draggedNodeKey,
            );
            if (rootIndex >= 0) {
              newTree.splice(rootIndex, 1);
            }
          }

          if (newParentKey === ROOT_PARENT_KEY || newParentKey === null) {
            draggedNode.parentKey = ROOT_PARENT_KEY;
            newTree.push(draggedNode);
          } else {
            const newParentNode = find(newTree, newParentKey);
            if (newParentNode) {
              draggedNode.parentKey = newParentKey;
              newParentNode.children = [
                ...(newParentNode.children || []),
                draggedNode,
              ];
            }
          }

          return newTree;
        });
      },
      [],
    );

    const onReorderSpaces = async (spaces: Space[]) => {
      setTreeLoading(true);

      const parentKey = spaces[0].parentId || ROOT_PARENT_KEY;

      const reorderedNodes = spaces.map((space, index) => {
        const node = find(spacesTree, space.id);
        return {
          ...(node || {}),
          key: space.id,
          name: space.name,
          guiIndex: index,
          nodeType: DisplayTreeNodeType.Space,
        } as DisplayTreeNode;
      });

      updateTreeForReorder(parentKey, reorderedNodes);

      await updateSpaces({
        variables: {
          ...project,
          spaces: spaces.map((s) => ({
            id: s.id,
            name: s.name,
            guiIndex: s.guiIndex,
            category: s.category,
            type: s.type,
          })),
        },
      });
    };

    const onReorderScenes = async (scenes: Scene[]) => {
      setTreeLoading(true);

      const spaceId = scenes[0].spaceId;
      const parentFolderKey = generateFolderName(spaceId, "scenes");

      const reorderedNodes = scenes.map((scene, index) => {
        const node = find(spacesTree, scene.id);
        return {
          ...(node || {}),
          key: scene.id,
          name: scene.name,
          guiIndex: index,
          nodeType: DisplayTreeNodeType.Scene,
        } as DisplayTreeNode;
      });

      updateTreeForReorder(parentFolderKey, reorderedNodes);

      await updateScenes({
        variables: {
          ...project,
          scenes: scenes.map((s) => ({
            id: s.id,
            name: s.name,
            guiIndex: s.guiIndex,
            spaceId: s.spaceId,
          })),
        },
      });
    };

    const onReparentSpace = async (
      spaceId: string,
      newParentId: string | null,
    ) => {
      setTreeLoading(true);

      const spaceNode = find(spacesTree, spaceId);
      if (spaceNode) {
        const oldParentKey = spaceNode.parentKey;
        updateTreeForReparent(spaceId, oldParentKey, newParentId);
      }

      await updateSpaceParent({
        variables: {
          ...project,
          spaceId,
          newParentId,
        },
      });
    };

    const onReparentScene = async (sceneId: string, newParentId: string) => {
      setTreeLoading(true);

      const sceneNode = find(spacesTree, sceneId);
      if (sceneNode) {
        const oldParentKey = sceneNode.parentKey;
        const newParentFolderKey = generateFolderName(newParentId, "scenes");
        updateTreeForReparent(sceneId, oldParentKey, newParentFolderKey);
      }

      await updateSceneParent({
        variables: {
          ...project,
          sceneId: sceneId,
          newSpaceId: newParentId,
        },
      });
    };

    const handleReparentSpace = async () => {
      if (draggedNodeInfo) {
        if (onReparentSpace) {
          await onReparentSpace(draggedNodeInfo.id, newParent?.id ?? null);
        }
        setConfirmationModalVisible(false);
      }
    };

    const handleReparentScene = async () => {
      if (draggedNodeInfo && newParent) {
        if (onReparentScene) {
          await onReparentScene(draggedNodeInfo.id, newParent?.id);
        }
        setConfirmationModalVisible(false);
      }
    };

    const onNodeDrop = async ({
      dragNode: draggedNode,
      node: targetNode,
      dropPosition,
      dropToGap: targetIsSibling,
    }: {
      dragNode: DisplayTreeNode;
      node: DisplayTreeNode; // The node below which the dragged node is dropped.
      dropPosition: number;
      dropToGap: boolean;
    }) => {
      const nodeData = checkNodeCanBeDropped(
        draggedNode,
        targetNode,
        targetIsSibling,
      );
      if (!nodeData) {
        return;
      }

      if (nodeData.action === "reparent") {
        setDraggedNodeInfo({
          id: draggedNode.key,
          name: draggedNode.name,
          nodeType: draggedNode.nodeType as
            | DisplayTreeNodeType.Scene
            | DisplayTreeNodeType.Space, // Allow type assertion here as node cannot be dragged if nodeType !== space or scene
        });
        setNewParent(
          nodeData.newParentId
            ? {
                id: nodeData.newParentId,
                name: find(spacesTree, nodeData.newParentId)!.name as string,
              }
            : null,
        );
        setConfirmationModalVisible(true);
        return;
      }

      dropPosition = targetIsSibling ? dropPosition : 0;
      const reorderedNodes = reorderNodes(
        draggedNode.key,
        nodeData.parentNodeChildren,
        dropPosition,
      );
      // Update guiIndex for each of the nodes.
      const updatedNodes = reorderedNodes.map((node, index) => ({
        ...node,
        guiIndex: index,
      }));

      if (draggedNode.nodeType === DisplayTreeNodeType.Space) {
        await onReorderSpaces(
          updatedNodes
            .filter((node) => node.nodeType === DisplayTreeNodeType.Space)
            .map((n) => ({
              ...spacesByIds[n.key],
              guiIndex: n.guiIndex,
            })) as Space[],
        );
      } else if (draggedNode.nodeType === DisplayTreeNodeType.Scene) {
        await onReorderScenes(
          updatedNodes
            .filter((node) => node.nodeType === DisplayTreeNodeType.Scene)
            .flatMap((n) => ({
              id: n.key,
              name: n.name,
              guiIndex: n.guiIndex,
              spaceId: nodeFolderKeyToId(n.parentKey, "scenes"),
            })) as Scene[],
        );
      }
    };

    const [debouncedSearchInput, setDebouncedSearchInput] = useState<
      SpaceSearchInput | undefined
    >(undefined);

    const debouncedSetSearchInput = useMemo(
      () =>
        _.debounce((value: SpaceSearchInput) => {
          setDebouncedSearchInput(value);
        }, 150),
      [],
    );

    useEffect(() => {
      if (searchInput) {
        debouncedSetSearchInput(searchInput);
      }
      return () => {
        debouncedSetSearchInput.cancel();
      };
    }, [searchInput, debouncedSetSearchInput]);

    const searchHits = useMemo(() => {
      if (debouncedSearchInput && !searchIsEmpty(debouncedSearchInput)) {
        return findSearchHits(
          spacesTree,
          debouncedSearchInput,
          spacesByIds,
          matches,
          spacesByIds,
        );
      }
    }, [debouncedSearchInput, spacesByIds, spacesTree]);

    // Async loading of nodes
    const [fetchShotsOnScene] = useGetShotsOnSceneLazyQuery({
      onError: (error) => {
        notify(`Failed to fetch shots on scene: ${error}`, "error");
      },
      onCompleted: ({ scene }) => {
        if (scene === undefined || scene === null) {
          return;
        }
        const shotNodes = transformShotsToDisplayTreeNode(
          scene.shots,
          scene.id,
          scene.name,
          fetchShotsOnScene,
        );

        const localVersion = version ? version + 1 : 1;

        if (setVersion && version) {
          setVersion(localVersion);
        }

        const localTree = updateTree(spacesTree, scene.id, shotNodes);
        localTree[0].version = localVersion;
        setSpacesTree(localTree);
        onLoadShots?.(scene.shots);

        if (setVersion && version) {
          setVersion(localVersion);
        }
        onLoadShots?.(scene.shots);
      },
    });

    const [fetchMappingsOnSpace] = useGetMappingsOnSpaceLazyQuery({
      fetchPolicy: "network-only",
      onError: (error) => {
        notify(`Failed to fetch mappings: ${error}`, "error");
      },
      onCompleted: ({ space }) => {
        if (!space?.mappings?.length) {
          return;
        }

        const mappings = space.mappings;
        const spaceId = space.id;

        // Update tree with mappings
        setSpacesTree((prevTree) => {
          const newTree = [...prevTree];
          const mappingNodes = transformMappingsToDisplayTreeNodes(
            mappings,
            spaceId,
          );
          const parentNodeKey = generateFolderName(spaceId, MAPPINGS_KEY);

          // Verify the mapping folder node exists in the tree
          const folderNodeExists = find(newTree, parentNodeKey);

          // If the folder node doesn't exist, we need to create it first
          if (!folderNodeExists) {
            const spaceNode = find(newTree, spaceId);
            if (spaceNode) {
              const mappingFolderNode =
                getMappingFolderDisplayTreeNode(spaceId);
              mappingFolderNode.children = mappingNodes;
              if (!spaceNode.children) {
                spaceNode.children = [];
              }
              spaceNode.children.unshift(mappingFolderNode);
              return newTree;
            }
          }

          const updatedTree = updateTree(newTree, parentNodeKey, mappingNodes);
          return updatedTree;
        });

        // Mark node as updated if needed
        if (nodeKeysToUpdate) {
          onNodeKeysUpdated?.([]);
        }
      },
    });

    // Update how we load mappings
    const onLoad = useCallback(
      async (loadedNode: DisplayTreeNode) => {
        if (loadedNode.loadAsync) {
          if (
            loadedNode.nodeType === DisplayTreeNodeType.Folder &&
            loadedNode.name === MAPPINGS_FOLDER_NAME &&
            showMappings // Make sure showMappings is true
          ) {
            // When a mapping folder is expanded, fetch mappings for that space only
            fetchMappingsOnSpace({
              variables: {
                ...project,
                spaceId: loadedNode.parentKey,
              },
            });
          } else if (loadedNode.nodeType === DisplayTreeNodeType.Scene) {
            fetchShotsOnScene({
              variables: {
                ...project,
                sceneId: loadedNode.key,
              },
            });
          }
        }
      },
      [fetchMappingsOnSpace, fetchShotsOnScene, project, showMappings],
    );

    const handleOnLoad = useCallback(
      async (loadedNode: DisplayTreeNode) => {
        await onLoad(loadedNode);
      },
      [onLoad],
    );

    useEffect(() => {
      nodeKeysToUpdate?.forEach(async (key) => {
        const node = findNode(spacesTree, key);
        if (
          node &&
          !(
            node.parentKey &&
            generateFolderName(node.parentKey, MAPPINGS_KEY) === node.key
          )
        ) {
          await onLoad(node);
        }
      });
    }, [nodeKeysToUpdate, onLoad, spacesTree]);

    const loadMappingsForSpaces = useCallback(() => {
      return;
    }, []);

    // Expose this method through useImperativeHandle or props
    useImperativeHandle(ref, () => ({
      loadMappingsForSpaces,
    }));

    if (!data) {
      return <Spin />;
    }
    return (
      <div
        className={classNames(styles["spaces-tree-root"], {
          [styles["scrollable-container"]]: scrollable,
        })}
      >
        {searchable && (
          <div className={styles["search-container"]}>
            <SearchBar project={project} onChange={setSearchInput} />
          </div>
        )}
        <div className={styles["tree-container-with-actions"]}>
          <div className={styles["tree-wrapper"]}>
            {showTagsSwitch ? (
              <TreeWithTagsSwitch
                treeData={spacesTree}
                showTags={showTags}
                setShowTags={setShowTags}
                onSelectNode={onSelect}
                editable={editable}
                checkable={checkable}
                disabled={treeLoading}
                onCheckNodes={handleOnCheck}
                onDropNode={onNodeDrop}
                onLoad={handleOnLoad}
                searchHits={externalSearchHits || searchHits}
                fillHeight={fillHeight}
              />
            ) : (
              <DisplayTree
                treeData={spacesTree}
                selectedKeys={selectedKeys}
                allowMultiSelect={allowMultiSelect}
                onSelectNode={onSelect}
                editable={editable}
                checkable={checkable}
                disabled={treeLoading}
                onCheckNodes={handleOnCheck}
                onDropNode={onNodeDrop}
                onLoad={handleOnLoad}
                searchHits={externalSearchHits || searchHits}
                fillHeight={fillHeight}
              />
            )}
          </div>
          <Tooltip title="Refresh tree data">
            <Button
              icon={
                <ReloadOutlined className={styles["refresh-button-icon"]} />
              }
              onClick={handleRefresh}
              loading={treeLoading || queryLoading}
              size="small"
              type="text"
              className={styles["refresh-button"]}
            />
          </Tooltip>
        </div>
        <SpacesTreeNodeReparentingModal
          isVisible={confirmationModalVisible}
          nodeType={draggedNodeInfo?.nodeType}
          onReparentNode={
            draggedNodeInfo?.nodeType === DisplayTreeNodeType.Space
              ? handleReparentSpace
              : handleReparentScene
          }
          onCancelReparentNode={() => setConfirmationModalVisible(false)}
          draggedNodeName={draggedNodeInfo?.name}
          targetNodeName={newParent?.name}
        />
      </div>
    );
  },
);

SpacesTreeComponent.displayName = "SpacesTree";

export const SpacesTree = SpacesTreeComponent;
export default SpacesTreeComponent;
